import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotify } from '../../../context/NotificationContext';
import { Card, Table, Input, Button, Typography, Tag } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import masterService from '../../../services/masterService';
import { useCommon } from '../../../context/CommonContext';

const { Title, Text } = Typography;
const { Search } = Input;

const ContentTypeList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();  
  // State management
  const [loading, setLoading] = useState(false);
  const [contentTypes, setContentTypes] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} content types`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    searchText: ''
  });

  const getContentTypes = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.searchText,
        ...params
      };
      
      console.log('Fetching content types with params:', requestParams);
      const response = await masterService.getContentTypes(requestParams);
      console.log('Content Types API Response:', response);
      
      if (response.data && response.data.status) {
        // Handle different possible response structures
        const contentTypesData = response.data.data?.data || response.data.data || [];
        const paginationData = response.data.data?.pagination || response.data.pagination || {};
        
        console.log('Content Types data:', contentTypesData);
        console.log('Pagination data:', paginationData);
        
        // Ensure contentTypesData is an array
        if (Array.isArray(contentTypesData)) {
          setContentTypes(contentTypesData);
        } else {
          console.error('Content Types data is not an array:', contentTypesData);
          setContentTypes([]);
        }
        
        setPagination(prev => ({
          ...prev,
          ...paginationData,
          current: paginationData.currentPage || 1
        }));
      } else {
            notify.error({ message: 'Failed to fetch content types' });
        setContentTypes([]);
      }
    } catch (error) {
      console.error('Error fetching content types:', error);
      notify.error({ message: 'Failed to fetch content types' });
      setContentTypes([]);
    } finally {
      setLoading(false);
    }
  }, [filters.searchText]);

  // Load content types data
  useEffect(() => {
    setPageTitle("Content Types");
    getContentTypes();
  }, [getContentTypes, setPageTitle]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, searchText: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getContentTypes({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getContentTypes({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current
    });
  };

  // Handle add content type
  const handleAddContentType = () => {
    navigate('/content-types/add/new');
  };

  // Handle edit content type
  const handleEditContentType = (ctId) => {
    navigate(`/content-types/edit/${ctId}`);
  };

  // Table columns
  const columns = [
    {
      title: 'Content Type',
      dataIndex: 'ct_name',
      key: 'ct_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.ct_name.localeCompare(b.ct_name),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'ct_is_active',
      key: 'ct_is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditContentType(record.ct_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info

  return (
      <Card>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <Title level={4} style={{ margin: 0 }}>
            Content Types ({pagination.total})
          </Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 300, flex: 1, justifyContent: 'flex-end' }}>
            <Search
              placeholder="Search content types..."
              allowClear
              onSearch={handleSearch}
              style={{ width: 320, maxWidth: '40vw' }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddContentType}
              size="middle"
            >
              Add Content Type
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={contentTypes}
          rowKey="ct_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{ 
            emptyText: 'No content types found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
  );
};

export default ContentTypeList;